import SelectMoreItemsHeader from 'app/components/selectMoreItems/SelectMoreItemsHeader';
import SearchTextInput from 'app/components/SearchTextInput';
import React, { FC, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Alert } from '@kontentino/ui';
import SelectSocialProfileOption, {
  SocialProfile,
} from './SelectSocialProfileOption';

type Props = {
  profiles: SocialProfile[];
  onSelect: (id: number | string) => void;
  selectedIds: (number | string)[];
  additionalHeader?: string;
  headerClassName?: string;
  isPartial?: boolean;
  onSelectAll?: (filteredIds: (number | string)[]) => void;
  onSelectNone?: (e: React.MouseEvent) => void;
};

const matchesSearch = (value: string, search: string) =>
  value.toLowerCase().includes(search);

const SelectSocialProfiles: FC<Props> = (props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const trimmedSearch = search.trim().toLowerCase();
  const isSearching = trimmedSearch.length > 0;

  const filteredProfiles = useMemo(() => {
    if (!trimmedSearch) {
      return props.profiles;
    }

    return props.profiles.filter(
      (profile) =>
        matchesSearch(profile.name, trimmedSearch) ||
        (profile.businessName
          ? matchesSearch(profile.businessName, trimmedSearch)
          : false),
    );
  }, [props.profiles, trimmedSearch]);

  const filteredIds = useMemo(
    () => filteredProfiles.map((profile) => profile.id),
    [filteredProfiles],
  );

  const selectedFilteredCount = useMemo(
    () => filteredIds.filter((id) => props.selectedIds.includes(id)).length,
    [filteredIds, props.selectedIds],
  );

  const hasBusinessInfo = useMemo(
    () => props.profiles.some((profile) => !!profile.businessId),
    [props.profiles],
  );

  const groupedProfiles = useMemo(() => {
    const otherLabel = t('newSocialMediaProfile.other');
    const groups = new Map<
      string,
      { label: string; isOther: boolean; profiles: SocialProfile[] }
    >();

    filteredProfiles.forEach((profile) => {
      const key = profile.businessId ?? otherLabel;
      const group = groups.get(key);

      if (group) {
        group.profiles.push(profile);
      } else {
        groups.set(key, {
          label: profile.businessId ? profile.businessName ?? '' : otherLabel,
          isOther: !profile.businessId,
          profiles: [profile],
        });
      }
    });

    return Array.from(groups.entries()).sort(([, a], [, b]) => {
      if (a.isOther) return 1;
      if (b.isOther) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [filteredProfiles, t]);

  const renderOption = (profile: SocialProfile) => {
    const isSelected = props.selectedIds.includes(profile.id);

    return (
      <SelectSocialProfileOption
        key={profile.id}
        profile={profile}
        isSelected={isSelected}
        onClick={() => {
          props.onSelect(profile.id);
        }}
      />
    );
  };

  const hasNoMatches = isSearching && filteredProfiles.length === 0;

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      {props.isPartial && (
        <Alert
          variant="warning"
          className="tw-mt-4"
          data-cy="select-social-profiles_partial-warning"
        >
          {t('newSocialMediaProfile.partialPagesWarning')}
        </Alert>
      )}
      <SelectMoreItemsHeader
        totalCount={filteredProfiles.length}
        selectedCount={selectedFilteredCount}
        onSelectAll={
          props.onSelectAll ? () => props.onSelectAll?.(filteredIds) : undefined
        }
        onSelectNone={props.onSelectNone}
        className={clsx('tw-mt-4', props.headerClassName)}
      />
      <SearchTextInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClear={() => setSearch('')}
        data-cy="select-social-profiles_search-input"
        data-name="select-social-profiles_search-input"
      />
      <div className="tw-flex tw-flex-col">
        {props.additionalHeader && (
          <div className="tw-mb-6 tw-rounded-md tw-bg-grayscale-5 tw-px-4 tw-py-[10px]">
            <span className="tw-text-md tw-font-medium tw-text-grayscale-180">
              {props.additionalHeader}
            </span>
          </div>
        )}
        {hasNoMatches ? (
          <div
            className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-text-center tw-text-md tw-text-grayscale-100"
            data-cy="select-social-profiles_no-matches"
          >
            {t('newSocialMediaProfile.noProfilesMatchSearch')}
          </div>
        ) : hasBusinessInfo ? (
          <div className="tw-flex tw-flex-col tw-gap-6">
            {groupedProfiles.map(([businessId, group]) => (
              <div key={businessId} className="tw-flex tw-flex-col tw-gap-6">
                <span
                  role="heading"
                  aria-level={3}
                  className="tw-text-md tw-font-medium tw-text-grayscale-180"
                  data-cy="select-social-profiles_business-header"
                >
                  {group.label}
                </span>
                <div className="tw-flex tw-flex-col tw-gap-6">
                  {group.profiles.map(renderOption)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-6">
            {filteredProfiles.map(renderOption)}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectSocialProfiles;
